/**
 * Type definitions for SDK v2
 */

// ============================================================================
// Locale Types
// ============================================================================

export type Locale = 'en' | 'de' | 'es' | 'fr' | 'it' | 'nl' | 'pl' | 'pt';

// ============================================================================
// Style Types
// ============================================================================

/**
 * Google Fonts the iframe can auto-load when the primary family name matches
 * (case/space insensitive). Appearance `fontFamily` accepts a full CSS stack
 * (e.g. `"Work Sans, system-ui, sans-serif"`).
 */
export type FontFamily =
  | 'DM Sans'
  | 'Inter'
  | 'Poppins'
  | 'Nunito'
  | 'Work Sans'
  | 'Manrope'
  | 'Rubik'
  | 'Karla'
  | 'Figtree'
  | 'Outfit'
  | 'Space Grotesk'
  | 'Urbanist';

export type FontWeight =
  | 100
  | 200
  | 300
  | 400
  | 500
  | 600
  | 700
  | 800
  | 900
  | 'normal'
  | 'bold'
  | 'lighter'
  | 'bolder';

export type Opacity = number | string; 

export interface InputContainerStyles {
  backgroundColor?: string;
  borderColor?: string;
  borderEnabled?: boolean;
  borderRadius?: string;
}

export interface InputStyles {
  backgroundColor?: string;
  borderColor?: string;
  borderEnabled?: boolean;
  borderRadius?: string;
  textColor?: string;
  placeholderColor?: string;
}

export interface ButtonBaseStyles {
  backgroundColor?: string;
  textColor?: string;
  borderRadius?: string;
  borderColor?: string;
  borderEnabled?: boolean;
  fontSize?: string;
  fontWeight?: FontWeight;
  opacity?: Opacity;
}

export interface ButtonStyles extends ButtonBaseStyles {
  hover?: ButtonBaseStyles;
  disabled?: ButtonBaseStyles;
  loaderColor?: string;
}

export interface GeneralMessageStyles {
  textColor?: string;
  backgroundColor?: string;
  borderEnabled?: boolean;
  borderRadius?: string;
  borderColor?: string;
}

export interface SuccessUIStyles {
  backgroundColor?: string;
  primaryTextColor?: string;
  secondaryTextColor?: string;
}

export interface ThemeStyles {
  formBackgroundColor?: string;
  inputContainer?: InputContainerStyles;
  input?: InputStyles;
  button?: ButtonStyles;
  disclaimerColor?: string;
  fieldErrorColor?: string;
  generalError?: GeneralMessageStyles;
  /** @deprecated No longer applied. Will be removed in a future major version. */
  generalSuccess?: GeneralMessageStyles;
  successUI?: SuccessUIStyles;
}

export type PaymentMethodLayout =
  | 'stacked'
  | 'verticalSelector'
  | 'horizontalSelector';

export type CardFieldLayout = 'compact' | 'split';

/** Payment method identifiers used in layout order / selection. */
export type PaymentMethodType = 'card' | 'apple_pay' | 'google_pay';

/** Theme tokens applied as CSS custom properties in the checkout iframe. */
export interface AppearanceVariables {
  /**
   * CSS `font-family` stack. Known Google Fonts in the primary slot are
   * auto-loaded; other fonts must already be available on the page.
   * @example "Inter, system-ui, sans-serif"
   */
  fontFamily?: string;
  /** Background of the checkout form box. Default: transparent. */
  formBackgroundColor?: string;
  /** Background of the iframe document (`html` / `body`). Default: transparent. */
  iframeBackgroundColor?: string;
  /** Surface background for inputs and related panels. */
  backgroundColor?: string;
  /** Primary text color. */
  textColor?: string;
  /** Secondary / muted text color (labels, disclaimer, helpers). */
  textSecondaryColor?: string;
  /** Input placeholder text color. */
  placeholderColor?: string;
  /** Brand / selection accent (selected payment method, related highlights). */
  primaryColor?: string;
  /** Text and icon color on primary-filled surfaces. */
  primaryTextColor?: string;
  /** Borders for payment-method layout, option rows, and dividers. */
  layoutBorderColor?: string;
  /** Card input border color. */
  inputBorderColor?: string;
  /** Card input focus border color. */
  inputFocusBorderColor?: string;
  /** Border radius for layout chrome (payment methods, form, banners, success UI). */
  layoutBorderRadius?: string;
  /** Border radius for card inputs and input wrappers. */
  inputBorderRadius?: string;
  /** Border radius for the card pay button and wallet (Apple Pay / Google Pay) buttons. */
  buttonBorderRadius?: string;
  /** Error text and field-error accent color. */
  dangerColor?: string;
  /** General error banner background color. */
  dangerBackgroundColor?: string;
  /** Success accent color (e.g. checkmark). */
  successColor?: string;
  /** Success fill surfaces (e.g. success icon circle). */
  successBackgroundColor?: string;
  /**
   * Card pay button background. Falls back to `primaryColor` when omitted.
   * Does not apply to wallet buttons — use `options.wallets.buttonTheme` instead.
   */
  buttonBackgroundColor?: string;
  /**
   * Card pay button text color.
   * Does not apply to wallet buttons — use `options.wallets.buttonTheme` instead.
   */
  buttonTextColor?: string;
  /**
   * Base color for the skeleton loader shimmer (parent page, while the iframe loads).
   * Any CSS color (hex, rgb, named like `red`, hsl, …). Default: light grey (`#e8e8e8`);
   * the highlight stop is derived by lightening this color.
   */
  loaderColor?: string;
}

/** Google Fonts stylesheet (`cssSrc` must be a fonts.googleapis.com / fonts.google.com HTTPS URL). */
export interface CssFontSource {
  cssSrc: string;
}

export interface PaymentElementAppearance {
  /** Theme tokens for colors, radii, and typography. */
  variables?: AppearanceVariables;
  /** Google Fonts stylesheets to load before applying `variables.fontFamily`. */
  fonts?: CssFontSource[];
}

export interface PaymentElementOptions {
  /**
   * How payment methods are arranged.
   * - `stacked` (default): methods listed vertically
   * - `verticalSelector`: selectable vertical method list
   * - `horizontalSelector`: selectable horizontal method tabs
   */
  paymentMethodLayout?: PaymentMethodLayout;
  /**
   * Display order for payment methods (and default selection).
   * First available method in this list is shown first and selected.
   * Unavailable methods are skipped; remaining enabled methods follow.
   * @example ['apple_pay', 'google_pay', 'card']
   */
  paymentMethodOrder?: PaymentMethodType[];
  /**
   * Per-session payment method enable/disable overrides, layered on top of
   * whatever the merchant has enabled in the dashboard. Methods not listed
   * keep their normal (dashboard-enabled) state — this can only narrow down
   * which methods show for this session, it can never force-enable a method
   * the merchant/admin hasn't authorized.
   *
   * Requesting wallet-only (Card disabled) is supported: if none of the
   * requested wallets are actually usable in the buyer's browser/device,
   * Card is shown anyway so the buyer is never left with zero payment
   * options.
   * @example { apple_pay: { enabled: false } } // hide Apple Pay for this session only
   * @example { card: { enabled: false }, apple_pay: { enabled: true } } // Apple Pay only, with automatic Card fallback
   */
  paymentMethods?: Partial<Record<PaymentMethodType, { enabled: boolean }>>;
  /**
   * Card field arrangement.
   * - `compact` (default): number / expiry / CVC in a compact group
   * - `split`: number on its own row; expiry and CVC side by side
   */
  cardFieldLayout?: CardFieldLayout;
  /** Stretch the element and iframe to 100% of the parent width (default fixed width otherwise). */
  fillParent?: boolean;
  /** Card pay button label. Default: "Complete Payment". Does not apply to wallet buttons. */
  buttonText?: string;
  /**
   * Wallet (Apple Pay / Google Pay) button options.
   *
   * Appearance color variables (`buttonBackgroundColor`, `buttonTextColor`,
   * `primaryColor`, etc.) do **not** apply to wallet buttons. The only Appearance
   * token shared with wallet buttons is `buttonBorderRadius`.
   *
   * Colors are controlled only via `buttonTheme` (black / white). Labels via
   * `buttonText` (or the localized default when omitted).
   */
  wallets?: {
    /**
     * Per-wallet button labels. When omitted, each button uses the localized
     * default equivalent of `"Pay"` for the active checkout locale.
     * Merchant-provided strings are used as-is (not translated).
     */
    buttonText?: {
      applePay?: string;
      googlePay?: string;
    };
    /**
     * Optional override for wallet button colors. Default: `auto` (recommended).
     * Only black and white are supported — not arbitrary brand colors.
     * Applies to both Apple Pay and Google Pay.
     *
     * Leave as `auto` unless you need to force a color:
     * - `auto`: black button (white text) on light system theme; white button
     *   (black text) on dark system theme
     * - `black`: black background, white text
     * - `white`: white background, black text
     */
    buttonTheme?: "auto" | "black" | "white";
  };
  /** Card form copy overrides. */
  cardForm?: {
    placeholders?: {
      cardNumber?: string;
      expiry?: string;
      cvc?: string;
    };
  };
  /** @deprecated Use `cardForm.placeholders`. */
  placeholders?: {
    cardNumber?: string;
    expiry?: string;
    cvc?: string;
  };
  /** Show the built-in success screen after payment. Default: `true`. */
  showDefaultSuccessUI?: boolean;
}

export interface CSSProperties {
  /** @deprecated Use `appearance.variables.fontFamily`. */
  fontFamily?: string;
  /** @deprecated Use `options.fillParent`. */
  fillParent?: boolean;
  formBackgroundColor?: string;
  inputContainer?: InputContainerStyles;
  input?: InputStyles;
  button?: ButtonStyles;
  disclaimerColor?: string;
  fieldErrorColor?: string;
  generalError?: GeneralMessageStyles;
  /** @deprecated No longer applied. Will be removed in a future major version. */
  generalSuccess?: GeneralMessageStyles;
  successUI?: SuccessUIStyles;
  /** @deprecated Use `appearance.variables` for theming instead of dark-mode blocks. */
  dark?: ThemeStyles;
}

// ============================================================================
// SDK Config Types
// ============================================================================

export interface SDKConfig {
  /** Public API key */
  publicKey?: string;
  /** Payment configuration */
  config?: PaymentConfig;
  /** Container element or selector where to mount the iframe (if not provided, creates a popup) */
  container?: string | HTMLElement;
  /** Locale for the UI. Defaults to 'en' */
  locale?: Locale;
  /** Callback when payment succeeds */
  onSuccess?: (data: TransactionData) => void;
  /** Callback when payment fails */
  onError?: (error: any) => void;
  /** Callback when user closes the payment modal */
  onClose?: () => void;
  /** Show default success UI after payment (default true). If false, only unmount iframe. */
  showDefaultSuccessUI?: boolean;
}

export interface PaymentConfig {
  name?: string;
  amount?: number;
  currency?: string;
  paymentId?: string;
  /** ID of a CustomerPaymentMethodRequest – activates save-card mode in the iframe */
  setupId?: string;
  /** Visual theme for the checkout form. Takes priority over `style` when set. */
  appearance?: PaymentElementAppearance;
  /** @deprecated Use `appearance`. Still applied when `appearance` is omitted. */
  style?: CSSProperties;
  /** Layout, width, button label, placeholders, and success UI settings. */
  options?: PaymentElementOptions;
  /** @deprecated Use `options.buttonText`. */
  buttonText?: string;
  /** @deprecated Use `options.cardForm.placeholders`. */
  placeholders?: {
    cardNumber?: string;
    expiry?: string;
    cvc?: string;
  };
  /** @deprecated Use `options.showDefaultSuccessUI`. */
  showDefaultSuccessUI?: boolean;
  [key: string]: any;
}

export interface IframeMessage {
  type: 'sdkData' | 'success' | 'error' | 'close' | '3ds-required' | 'iframe-ready' | 'content-height' | '3ds-failed' | '3ds-success' | 'apple-pay-sheet';
  /**
   * Outbound `sdkData`: `{ publicKey, pageReferrer? }` — `pageReferrer` is the
   * merchant page's `document.referrer` (Inflow keeps the origin only).
   */
  data?: any;
  config?: PaymentConfig;
  threeDsSessionUrl?: string; // URL for 3DS challenge when type is '3ds-required'
  paymentId?: string; // Payment ID for payment flow
  setupId?: string; // CustomerPaymentMethodRequest ID for save-card flow
  sessionId?: string; // 3DS session ID when type is '3ds-completed'
  success?: boolean; // 3DS result when type is '3ds-result'
  height?: number; // Content height when type is 'content-height'
  open?: boolean; // Whether Apple's QR sheet is open in the iframe when type is 'apple-pay-sheet'
}

export interface TransactionData {
  transaction?: {
    id: string;
    amount: number;
    currency: string;
    status: string;
    created_at: string;
    [key: string]: any;
  };
  [key: string]: any;
}

// ============================================================================
// Payment Result Types
// ============================================================================

export enum PaymentResultStatus {
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}

export enum PaymentResultErrorCode {
  THREE_DS_FAILED = 'THREE_DS_FAILED',
  PAYMENT_PROCESSING_ERROR = 'PAYMENT_PROCESSING_ERROR',
  /**
   * The checkout iframe never reported itself ready (see the readiness timeout
   * in `sdk.ts`). Usually the browser refused to render the frame because the
   * embedding domain is not verified on the merchant's Inflow account.
   * Lower-case on the wire on purpose — it is the documented public code.
   */
  IFRAME_NOT_READY = 'iframe_not_ready',
}

export interface PaymentError {
  code: PaymentResultErrorCode;
  message: string;
  retryable: boolean;
  /**
   * Origin of the page the SDK was embedded in, when it is part of the
   * diagnosis (set for `iframe_not_ready`: it is the domain to verify).
   */
  origin?: string;
}

export interface PaymentResult {
  status: PaymentResultStatus;
  paymentId: string;
  error?: PaymentError;
}

export interface SaveCardResult {
  status: PaymentResultStatus;
  setupId: string;
  error?: PaymentError;
}

export interface CardElementState {
  complete: boolean;
}

